import { motion } from 'framer-motion';
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  History,
  Info,
  Wallet,
} from 'lucide-react';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AttendanceCard, ClockInButton } from '@/components/attendance';
import { useAttendance, useAuth } from '@/hooks';
import { attendanceService } from '@/services';
import { checkClockInTimeRestriction, formatMinutes, formatNaira, formatTime } from '@/utils';
import { cn } from '@/utils/cn';
import { motivationalMessages } from '@/utils/motivational-messages';
import { isEvening, isFriday, isNight, isSunday } from '@/utils/time-tracker';

export function StaffDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    todayAttendance,
    summary,
    attendanceRecords,
    currentPayment,
    isLoading: attendanceLoading,
    error: attendanceError,
    fetchTodayAttendance,
    fetchMyAttendance,
    fetchMySummary,
    fetchPaymentRecord,
  } = useAttendance(false);
  const [dailyMessage, setDailyMessage] = useState<string>(
    motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)],
  );
  const [loading, setLoading] = useState(true);
  const [timeRestriction, setTimeRestriction] = useState<{
    isAllowed: boolean;
    message?: string;
  } | null>(null);
  const [showFinancialData, setShowFinancialData] = useState(false);
  const [mostPunctualStaff, setMostPunctualStaff] = useState<string | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const keepChanging = true;
      while (keepChanging) {
        const newMessage =
          motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
        if (newMessage === dailyMessage) {
          continue;
        }
        setDailyMessage(newMessage);
        break;
      }
    }, 30000);
    return () => clearTimeout(timeout);
  }, [dailyMessage]);

  useEffect(() => {
    const restriction = checkClockInTimeRestriction(todayAttendance?.clockedIn ?? false);
    setTimeRestriction(restriction);
  }, [todayAttendance?.clockedIn]);

  useEffect(() => {
    const refreshMostPunctual = async () => {
      if (todayAttendance?.clockedIn) {
        try {
          const now = new Date();
          const result = await attendanceService.getMostPunctualStaff(
            now.getFullYear(),
            now.getMonth() + 1,
          );
          setMostPunctualStaff(result.name);
        } catch (error) {}
      }
    };
    refreshMostPunctual();
  }, [todayAttendance?.clockedIn]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const [_, mostPunctual] = await Promise.all([
          Promise.all([
            fetchTodayAttendance(),
            fetchMySummary(now.getFullYear(), now.getMonth() + 1),
            fetchMyAttendance(),
            fetchPaymentRecord(),
          ]),
          attendanceService.getMostPunctualStaff(now.getFullYear(), now.getMonth() + 1),
        ]);
        setMostPunctualStaff(mostPunctual.name);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchTodayAttendance, fetchMySummary, fetchMyAttendance, fetchPaymentRecord]);

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const cantClockinMessage = isSunday()
    ? ''
    : isEvening()
      ? 'You should be home by now!'
      : isNight()
        ? "It's too early!"
        : 'Clock-in time passed!';

  const formattedClockInTimestamp = todayAttendance?.attendance?.clockInTimestamp
    ? formatTime(todayAttendance.attendance.clockInTimestamp)
    : '--:--';

  if (loading || attendanceLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-3">
        <div className="w-10 h-10 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
        <p className="text-slate-400 font-bold text-sm animate-pulse">Syncing dashboard...</p>
      </div>
    );
  }

  const stats = [
    {
      label: 'Days Present',
      value: summary?.totalDays || 0,
      icon: Calendar,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
    },
    {
      label: 'On Time',
      value: summary?.onTimeDays || 0,
      icon: CheckCircle,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'Late Arrivals',
      value: summary?.lateDays || 0,
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
    },
    {
      label: 'Total Deductions',
      value: formatNaira(summary?.totalDeductions || 0),
      icon: Wallet,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10',
      trend: 'Current month',
      isMonetary: true,
    },
  ];
  const cantClockIn =
    timeRestriction &&
    !timeRestriction.isAllowed &&
    todayAttendance?.canClockIn &&
    !todayAttendance?.clockedIn;
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="flex items-center gap-2 text-neolab-green font-black text-xs uppercase tracking-[0.2em] mb-1">
              <Activity className="w-4 h-4" />
              <span>Operational Status: Active</span>
            </div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Hello, {user?.profile?.firstName}! 👋
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-bold">
              It's {currentDate}.{' '}
              {isFriday() && <span className="text-gradient-brand font-black">TGIF! </span>}
              <span className="text-gradient-brand">{dailyMessage}!</span>
            </p>
          </motion.div>
        </div>

        {user && !user.bankDetails && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-700 dark:text-amber-300 font-bold mb-2">
                Bank details not set
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-3">
                Please set up your bank details to receive payments. Navigate to the Pay Checks page
                to add your account information.
              </p>
              <button
                type="button"
                onClick={() => navigate('/pay-checks')}
                className="px-4 py-2 rounded-xl bg-amber-600 text-white font-black text-xs hover:bg-amber-700 transition-all cursor-pointer"
              >
                Go to Pay Checks
              </button>
            </div>
          </motion.div>
        )}

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={() => setShowFinancialData(!showFinancialData)}
            className="p-2 rounded-lg -mt-12 -mb-12 hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
            title={showFinancialData ? 'Hide financial data' : 'Show financial data'}
          >
            {showFinancialData ? (
              <Eye className="w-5 h-5 text-neolab-green" />
            ) : (
              <EyeOff className="w-5 h-5 text-slate-400" />
            )}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {(() => {
            const salary = user?.attendanceConfig?.salary || 0;
            const allowance = user?.attendanceConfig?.allowance || 0;
            const grossSalary = salary + allowance;
            const netIncome = currentPayment ? currentPayment.netIncome : grossSalary;

            const boxes = [
              {
                label: 'Monthly Salary',
                value: formatNaira(salary),
                icon: Wallet,
                color: 'text-blue-500',
                bg: 'bg-blue-500/10',
              },
            ];

            if (allowance > 0) {
              boxes.push({
                label: 'Allowance',
                value: formatNaira(allowance),
                icon: Wallet,
                color: 'text-purple-500',
                bg: 'bg-purple-500/10',
              });
              boxes.push({
                label: 'Gross Salary',
                value: formatNaira(grossSalary),
                icon: Wallet,
                color: 'text-emerald-500',
                bg: 'bg-emerald-500/10',
              });
            }

            boxes.push({
              label: 'Net Income',
              value: formatNaira(netIncome),
              icon: Wallet,
              color: 'text-neolab-green',
              bg: 'bg-neolab-green/10',
            });

            return boxes.map((box, index) => {
              const Icon = box.icon;
              return (
                <motion.div
                  key={box.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="px-3 py-2.5 sm:px-4 rounded-2xl glass-card dark:glass-card-dark shadow-sm border border-neolab-green/5 dark:border-white/5"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] sm:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest truncate">
                        {box.label}
                      </p>
                      <p className={`text-base sm:text-lg font-black ${box.color} truncate`}>
                        {showFinancialData ? box.value : '••••••'}
                      </p>
                    </div>
                    <div className="hidden sm:block w-px h-6 bg-neolab-green/10 dark:bg-white/10 flex-shrink-0" />
                    <div className={`p-1.5 sm:p-2 rounded-xl ${box.bg} shadow-lg flex-shrink-0`}>
                      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${box.color}`} />
                    </div>
                  </div>
                </motion.div>
              );
            });
          })()}
        </div>

        {mostPunctualStaff && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-3 p-3 rounded-xl glass-card dark:glass-card-dark shadow-sm border border-neolab-green/10 dark:border-white/10 bg-gradient-to-r from-neolab-green/5 to-emerald-500/5 dark:from-neolab-green/10 dark:to-emerald-500/10"
          >
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-neolab-green/10 dark:bg-neolab-green/20">
                <Award className="w-4 h-4 text-neolab-green" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                  Most Punctual Staff 🎉🎉🎉🎉🎉🎉🎉🎉
                </p>
                <p className="text-sm font-black text-slate-900 dark:text-white">
                  {mostPunctualStaff}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {attendanceError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3.5 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-rose-700 dark:text-rose-300 font-bold">{attendanceError}</p>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="relative overflow-hidden rounded-xl bg-white dark:bg-neolab-forest p-4 lg:p-6 text-slate-900 dark:text-white shadow-lg shadow-neolab-green/5 dark:shadow-none border border-neolab-green/5 dark:border-white/5"
      >
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-slate-50 dark:from-accent-orange/5 to-transparent pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neolab-green/5 dark:bg-white/10 backdrop-blur-md border border-neolab-green/10 dark:border-white/10 mb-4">
              <div className="w-2 h-2 rounded-full bg-neolab-green animate-pulse" />
              <span className="text-xs font-black uppercase tracking-[0.2em] text-neolab-green dark:text-white/80">
                Attendance System
              </span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black mb-3 leading-tight text-slate-900 dark:text-white">
              {todayAttendance?.clockedIn &&
              !todayAttendance?.clockedOut &&
              new Date().getHours() >= 20 ? (
                <>
                  Clockout <span className="text-amber-500">missed</span>
                </>
              ) : todayAttendance?.clockedIn && !todayAttendance?.clockedOut ? (
                <>
                  Shift <span className="text-neolab-green">Started</span>
                </>
              ) : todayAttendance?.clockedIn && todayAttendance?.clockedOut ? (
                <>
                  Shift <span className="text-neolab-green">Ended</span>
                </>
              ) : cantClockIn ? (
                <span className="text-amber-500">{cantClockinMessage}</span>
              ) : (
                <>
                  Ready to start your <span className="text-neolab-green">Shift?</span>
                </>
              )}
            </h2>

            {cantClockIn && (
              <div className="mb-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs font-black text-amber-700 dark:text-amber-300 text-center">
                  {timeRestriction?.message}
                </p>
              </div>
            )}

            {todayAttendance?.clockedIn && (
              <div className="mb-4 p-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border border-neolab-green/10 dark:border-white/10 backdrop-blur-sm inline-block w-full lg:w-auto">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-neolab-green/20">
                    <CheckCircle className="w-5 h-5 text-neolab-green" />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-black text-slate-400 dark:text-white/50 uppercase tracking-widest">
                      Clocked In At
                    </p>
                    <p className="text-xl font-black text-slate-900 dark:text-white">
                      {formattedClockInTimestamp}
                    </p>
                  </div>
                </div>
                {todayAttendance.attendance?.minutesLate ? (
                  <div className="mt-3 px-3 py-1.5 rounded-lg bg-rose-500 border border-rose-500/10 text-rose-100 text-[11px] font-black uppercase tracking-wider inline-flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Late by {formatMinutes(todayAttendance.attendance.minutesLate)}
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex flex-wrap justify-center lg:justify-start gap-3">
              {todayAttendance?.canClockIn && (
                <ClockInButton type="clock-in" timeRestriction={timeRestriction} />
              )}
              {todayAttendance?.canClockOut && new Date().getHours() < 20 && (
                <ClockInButton type="clock-out" timeRestriction={timeRestriction} />
              )}
              {todayAttendance?.clockedOut && (
                <div className="flex items-center gap-2.5 px-6 py-3 bg-emerald-500 rounded-lg text-white shadow-lg shadow-emerald-500/30">
                  <CheckCircle className="w-6 h-6" />
                  <span className="font-black text-lg">Day Completed</span>
                </div>
              )}
            </div>
          </div>

          <div className="hidden xl:block w-48 h-48 relative">
            <div className="absolute inset-0 bg-neolab-green/10 dark:bg-neolab-green/10 rounded-full animate-ping opacity-10" />
            <div className="absolute inset-2 border-2 border-dashed border-slate-200 dark:border-white/20 rounded-full animate-[spin_20s_linear_infinite]" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-5xl font-black text-slate-900 dark:text-white mb-1">
                  {currentTime.split(' ')[0]}
                </p>
                <p className="text-lg font-black text-neolab-green uppercase tracking-widest">
                  {currentTime.split(' ')[1]}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.1 }}
              className="group p-4 rounded-xl glass-card dark:glass-card-dark shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <div
                  className={cn(
                    'p-2.5 rounded-lg transition-transform group-hover:scale-110 duration-300',
                    stat.bg,
                  )}
                >
                  <Icon className={cn('w-5 h-5', stat.color)} />
                </div>
                {stat.trend && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-500 dark:bg-green-800 text-xs font-black text-white uppercase tracking-tighter">
                    <ArrowUpRight className="w-3 h-3" />
                    {stat.trend}
                  </div>
                )}
              </div>
              <div>
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-1">
                  {stat.label}
                </p>
                <p
                  className={cn(
                    'text-2xl font-black tracking-tight',
                    stat.isMonetary ? stat.color : 'text-slate-900 dark:text-white',
                  )}
                >
                  {stat.value}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-white dark:bg-neolab-forest shadow-sm">
              <History className="w-5 h-5 text-neolab-green" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                Activity History
              </h2>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                Recent logs
              </p>
            </div>
          </div>
          <button
            type="button"
            className="px-4 py-1.5 rounded-lg bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-[10px] uppercase tracking-widest hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
          >
            View All
          </button>
        </div>

        <div className="p-3">
          {attendanceRecords.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-10 h-10 text-slate-300 dark:text-slate-600" />
              </div>
              <p className="text-slate-500 dark:text-slate-400 font-bold text-lg">
                No records found
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {attendanceRecords.slice(0, 5).map((record, i) => (
                <motion.div
                  key={record._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.1 }}
                >
                  <AttendanceCard attendance={record} showDetails={false} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default StaffDashboard;
